import {
    BankOutlined,
    CalendarOutlined,
    CodeOutlined,
    EnvironmentOutlined,
    GlobalOutlined,
    MailOutlined,
    PhoneOutlined,
    SafetyCertificateOutlined,
    TeamOutlined,
    UploadOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {
    Avatar,
    Button,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Tag,
    Typography,
    Upload,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { API_URL } from "../../variables/constants";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Text } = Typography;
const { Option } = Select;

const AVATAR_COLORS = [
    "#1677ff", "#52c41a", "#fa8c16", "#722ed1",
    "#eb2f96", "#13c2c2", "#f5222d", "#2f54eb",
];
const avatarColor = (str = "") =>
    AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];

const SECTION_LABEL = {
    fontSize: 11,
    fontWeight: 700,
    color: "#8c8c8c",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 6,
};

const FIELD_LABEL = { color: "#595959", fontWeight: 600, fontSize: 13 };
const REQ = <span style={{ color: "#ff4d4f" }}> *</span>;

const AddMembres = ({ show, handleClose, editMode, selectedMembre }) => {
    const [raisonSociale, setRaisonSociale] = useState("");
    const [siegeSocial, setSiegeSocial] = useState("");
    const [code, setCode] = useState("");
    const [dateCreation, setDateCreation] = useState(null);
    const [email, setEmail] = useState("");
    const [telephone1, setTelephone1] = useState("");
    const [telephone2, setTelephone2] = useState("");
    const [typeMembre, setTypeMembre] = useState("ASSURANCE");
    const [responsable, setResponsable] = useState("");
    const [otpConnexion, setOtpConnexion] = useState("NONE");
    const [domaine, setDomaine] = useState("");
    const [website, setWebsite] = useState("");
    const [fileImage, setFileImage] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (editMode && selectedMembre?.organismeId) {
            axiosInstance
                .get(`/api/organisme/${selectedMembre.organismeId}`)
                .then(({ data: o }) => {
                    setRaisonSociale(o.raisonSociale || "");
                    setSiegeSocial(o.siegeSocial || "");
                    setCode(o.code || "");
                    setDateCreation(o.dateCreation ? dayjs(o.dateCreation) : null);
                    setEmail(o.email || "");
                    setTelephone1(o.contact?.[0] || "");
                    setTelephone2(o.contact?.[1] || "");
                    setTypeMembre(o.typeOrganisme || "ASSURANCE");
                    setResponsable(o.responsable || "");
                    setOtpConnexion(o.otpConnexion || "NONE");
                    setDomaine(o.domaine || "");
                    setWebsite(o.website || "");
                    if (o.logo) {
                        setImagePreviewUrl(
                            `${API_URL}/api/organisme/logo/${o.logo.split("/")[1]}`
                        );
                    }
                })
                .catch(() =>
                    toast.error("Erreur lors du chargement des informations du membre.")
                );
        }
    }, [editMode, selectedMembre]);

    const handleImageChange = (file) => {
        setFileImage(file);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreviewUrl(reader.result);
        reader.readAsDataURL(file);
        return false;
    };

    const handleSubmit = async () => {
        if (!raisonSociale || !siegeSocial || !code || !dateCreation || !email || !telephone1) {
            toast.error("Veuillez remplir tous les champs obligatoires.");
            return;
        }
        setLoading(true);

        const organisme = {
            ...selectedMembre,
            raisonSociale,
            siegeSocial,
            code,
            dateCreation: dayjs(dateCreation).format("YYYY-MM-DD"),
            contact: [telephone1, telephone2].filter(Boolean),
            email,
            responsable,
            typeOrganisme: typeMembre,
            otpConnexion,
            domaine,
            website,
            active: true,
        };

        try {
            if (editMode && selectedMembre?.organismeId) {
                await axiosInstance.put(`/api/organisme`, organisme);
                toast.success("Membre mis à jour avec succès !");
            } else {
                await axiosInstance.post(`/api/organisme`, organisme);
                toast.success("Nouveau membre ajouté avec succès !");
            }

            if (fileImage) {
                const formData = new FormData();
                const ext = fileImage.name.split(".").pop();
                formData.append(
                    "fichier",
                    fileImage,
                    `Logo_${raisonSociale.split(" ").join("-")}.${ext}`
                );
                await axiosMultiPartDataInstance.post(`/api/organisme/logo`, formData);
            }

            window.location.reload();
            handleClose();
        } catch {
            toast.error("Erreur lors de l'enregistrement des données.");
        } finally {
            setLoading(false);
        }
    };

    const initials = raisonSociale
        ? raisonSociale.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase()).join("")
        : null;

    const TYPE_LABEL = { ASSURANCE: "Assureur", PARTENAIRE: "Partenaire", CONCESSIONNAIRE: "Concessionnaire" };

    return (
        <Modal
            open={show}
            onCancel={handleClose}
            title={
                <Space size={12} align="center">
                    <div
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: editMode ? "#fff7e6" : "#e6f4ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {editMode
                            ? <BankOutlined style={{ color: "#fa8c16", fontSize: 19 }} />
                            : <TeamOutlined style={{ color: "#1677ff", fontSize: 19 }} />
                        }
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "#262626", lineHeight: 1.3 }}>
                            {editMode ? "Modifier le membre" : "Nouveau membre"}
                        </div>
                        <div style={{ fontSize: 12, color: "#8c8c8c", fontWeight: 400 }}>
                            {editMode
                                ? "Mettre à jour les informations"
                                : "Ajouter une compagnie au Pool"}
                        </div>
                    </div>
                </Space>
            }
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button onClick={handleClose} style={{ borderRadius: 8 }}>
                        Annuler
                    </Button>
                    <Button
                        type="primary"
                        loading={loading}
                        onClick={handleSubmit}
                        style={{
                            borderRadius: 8,
                            fontWeight: 600,
                            minWidth: 130,
                            background: editMode
                                ? "linear-gradient(135deg, #fa8c16, #fa541c)"
                                : "linear-gradient(135deg, #1677ff, #0950a8)",
                            border: "none",
                        }}
                    >
                        {loading
                            ? "En cours..."
                            : editMode
                                ? "Mettre à jour"
                                : "Ajouter le membre"}
                    </Button>
                </div>
            }
            width={700}
            centered
            destroyOnClose
            styles={{
                body: {
                    padding: "20px 24px",
                    maxHeight: "72vh",
                    overflowY: "auto",
                },
            }}
        >
            {/* Identity banner — apparaît dès que la raison sociale est saisie */}
            {raisonSociale && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        background: "#f0f7ff",
                        borderRadius: 10,
                        padding: "10px 14px",
                        marginBottom: 20,
                        border: "1px solid #bae0ff",
                    }}
                >
                    <Avatar
                        size={40}
                        style={{
                            backgroundColor: avatarColor(raisonSociale),
                            fontWeight: 700,
                            fontSize: 15,
                            flexShrink: 0,
                        }}
                    >
                        {initials}
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                        <Text strong style={{ fontSize: 14, color: "#0050b3", display: "block" }}>
                            {raisonSociale}
                        </Text>
                        <Space size={6} style={{ marginTop: 2 }}>
                            {code && (
                                <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                                    {code}
                                </Tag>
                            )}
                            <Tag
                                style={{
                                    fontSize: 11,
                                    margin: 0,
                                    color: typeMembre === "ASSURANCE" ? "#1677ff" : "#52c41a",
                                    background: typeMembre === "ASSURANCE" ? "#e6f4ff" : "#f6ffed",
                                    border: `1px solid ${typeMembre === "ASSURANCE" ? "#91caff" : "#b7eb8f"}`,
                                }}
                            >
                                {TYPE_LABEL[typeMembre] || typeMembre}
                            </Tag>
                        </Space>
                    </div>
                </div>
            )}

            <Form layout="vertical" requiredMark={false} component="div">

                {/* ── Section : Identification ── */}
                <div style={SECTION_LABEL}>
                    <BankOutlined /> Identification
                </div>
                <Row gutter={16}>
                    <Col span={16}>
                        <Form.Item label={<span style={FIELD_LABEL}>Dénomination sociale{REQ}</span>}>
                            <Input
                                placeholder="Ex. : Assurance Nationale"
                                value={raisonSociale}
                                onChange={(e) => setRaisonSociale(e.target.value)}
                                prefix={<BankOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label={<span style={FIELD_LABEL}>Code{REQ}</span>}>
                            <Input
                                placeholder="Ex. : AN"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                prefix={<CodeOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Siège social{REQ}</span>}>
                            <Input
                                placeholder="Ville, Pays"
                                value={siegeSocial}
                                onChange={(e) => setSiegeSocial(e.target.value)}
                                prefix={<EnvironmentOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Date de création{REQ}</span>}>
                            <DatePicker
                                value={dateCreation}
                                onChange={setDateCreation}
                                format="DD/MM/YYYY"
                                placeholder="jj/mm/aaaa"
                                style={{ width: "100%", borderRadius: 8 }}
                                suffixIcon={<CalendarOutlined style={{ color: "#bfbfbf" }} />}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Type de membre</span>}>
                            <Select
                                value={typeMembre}
                                onChange={setTypeMembre}
                                style={{ borderRadius: 8 }}
                            >
                                <Option value="ASSURANCE">Assureur</Option>
                                <Option value="PARTENAIRE">Partenaire</Option>
                                <Option value="CONCESSIONNAIRE">Concessionnaire</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Responsable</span>}>
                            <Input
                                placeholder="Nom du responsable"
                                value={responsable}
                                onChange={(e) => setResponsable(e.target.value)}
                                prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={{ margin: "4px 0 16px" }} />

                {/* ── Section : Contact ── */}
                <div style={SECTION_LABEL}>
                    <PhoneOutlined /> Contact
                </div>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Email{REQ}</span>}>
                            <Input
                                type="email"
                                placeholder="contact@compagnie.tg"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Téléphone 1{REQ}</span>}>
                            <Input
                                placeholder="+228 XX XX XX XX"
                                value={telephone1}
                                onChange={(e) => setTelephone1(e.target.value)}
                                prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Téléphone 2</span>}>
                            <Input
                                placeholder="+228 XX XX XX XX"
                                value={telephone2}
                                onChange={(e) => setTelephone2(e.target.value)}
                                prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Site web</span>}>
                            <Input
                                placeholder="https://www.compagnie.tg"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                prefix={<GlobalOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={{ margin: "4px 0 16px" }} />

                {/* ── Section : Configuration ── */}
                <div style={SECTION_LABEL}>
                    <SafetyCertificateOutlined /> Configuration
                </div>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Authentification OTP</span>}>
                            <Select
                                value={otpConnexion}
                                onChange={setOtpConnexion}
                                style={{ borderRadius: 8 }}
                            >
                                <Option value="NONE">Désactivé</Option>
                                <Option value="SMS">SMS</Option>
                                <Option value="EMAIL">Email</Option>
                                <Option value="WHATSAPP">WhatsApp</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<span style={FIELD_LABEL}>Nom de domaine</span>}>
                            <Input
                                placeholder="compagnie.poolins.tg"
                                value={domaine}
                                onChange={(e) => setDomaine(e.target.value)}
                                prefix={<GlobalOutlined style={{ color: "#bfbfbf" }} />}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={{ margin: "4px 0 16px" }} />

                {/* ── Section : Logo ── */}
                <div style={SECTION_LABEL}>
                    <UploadOutlined /> Logo
                </div>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <div
                        style={{
                            width: 80,
                            height: 80,
                            borderRadius: 12,
                            border: "1px solid #f0f0f0",
                            background: "#fafafa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            flexShrink: 0,
                        }}
                    >
                        {imagePreviewUrl ? (
                            <img
                                src={imagePreviewUrl}
                                alt="Aperçu"
                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                            />
                        ) : (
                            <BankOutlined style={{ fontSize: 28, color: "#d9d9d9" }} />
                        )}
                    </div>

                    <Upload
                        accept="image/png,image/jpeg"
                        showUploadList={false}
                        beforeUpload={handleImageChange}
                        style={{ flex: 1 }}
                    >
                        <div
                            style={{
                                border: "2px dashed #d9d9d9",
                                borderRadius: 10,
                                padding: "18px 24px",
                                textAlign: "center",
                                cursor: "pointer",
                                background: "#fafafa",
                                transition: "border-color 0.2s, background 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = "#1677ff";
                                e.currentTarget.style.background = "#f0f7ff";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = "#d9d9d9";
                                e.currentTarget.style.background = "#fafafa";
                            }}
                        >
                            <UploadOutlined style={{ fontSize: 20, color: "#1677ff", marginBottom: 4 }} />
                            <div style={{ color: "#595959", fontSize: 13, fontWeight: 500 }}>
                                {imagePreviewUrl ? "Changer le logo" : "Cliquer pour téléverser"}
                            </div>
                            <div style={{ color: "#bfbfbf", fontSize: 11, marginTop: 2 }}>
                                PNG ou JPEG · max 2 Mo
                            </div>
                        </div>
                    </Upload>
                </div>
            </Form>
        </Modal>
    );
};

export default AddMembres;
